// 📄 pages/Soporte/SoporteHome.tsx
// Hub de soporte (solo Admin de plataforma) — buscador único sobre
// usuarios/organizaciones/proyectos + detalle de la persona/org/proyecto
// elegido, con el historial de incidentes reportados por esa persona
// cruzando CUALQUIER proyecto. No duplica pantallas ya existentes (gestión
// de miembros de org, detalle de proyecto) — es un punto de entrada rápido
// que lleva a esas mismas vistas (ver botón "Entrar", reusa
// esAdminObservador de ProjectContext.tsx sin ningún cambio).
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';
import { Search, LifeBuoy, User, Building2, FolderKanban, LogIn, AlertCircle, RefreshCw } from 'lucide-react';
import { UserProfile, listPlatformUsers } from '../../services/userService.ts';
import { OrganizacionSinRol, listAllOrganizationsAdmin, getUserOrganizations, getOrgMembers, OrgMemberConNombre } from '../../services/organizationService.ts';
import { Project, listAllProjects } from '../../services/projectService.ts';
import { IncidenteConProyecto, buscarIncidentesPorEmailAdmin } from '../../services/incidenteService.ts';
import { Ticket, TicketEstado, TICKET_ESTADOS, getAllTicketsAdmin, updateTicketEstado } from '../../services/ticketService.ts';
import TicketConversacion from './TicketConversacion.tsx';
import { Organization } from '../../types/organization.ts';

type Seleccion =
  | { tipo: 'usuario'; item: UserProfile }
  | { tipo: 'organizacion'; item: OrganizacionSinRol }
  | { tipo: 'proyecto'; item: Project };

const MAX_RESULTADOS = 15;

export default function SoporteHome() {
  // Respaldo por si alguien llega directo por URL sin pasar por el ítem
  // del navbar (que ya se oculta para no-admin) — mismo criterio que
  // EditarProyecto.tsx.
  const { profile } = useAuth();
  const [usuarios, setUsuarios] = useState<UserProfile[]>([]);
  const [organizaciones, setOrganizaciones] = useState<OrganizacionSinRol[]>([]);
  const [proyectos, setProyectos] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [seleccion, setSeleccion] = useState<Seleccion | null>(null);

  useEffect(() => {
    Promise.all([listPlatformUsers(), listAllOrganizationsAdmin(), listAllProjects()])
      .then(([u, o, p]) => { setUsuarios(u); setOrganizaciones(o); setProyectos(p); })
      .catch((err) => console.error('Error cargando datos de Soporte:', err))
      .finally(() => setLoading(false));
  }, []);

  const q = busqueda.trim().toLowerCase();
  const hayBusqueda = q.length > 0;

  const usuariosFiltrados = useMemo(() => {
    const lista = hayBusqueda
      ? usuarios.filter((u) => u.email.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q))
      : usuarios;
    return lista.slice(0, MAX_RESULTADOS);
  }, [usuarios, q, hayBusqueda]);

  const organizacionesFiltradas = useMemo(() => {
    const lista = hayBusqueda ? organizaciones.filter((o) => o.name.toLowerCase().includes(q)) : organizaciones;
    return lista.slice(0, MAX_RESULTADOS);
  }, [organizaciones, q, hayBusqueda]);

  const proyectosFiltrados = useMemo(() => {
    const lista = hayBusqueda
      ? proyectos.filter((p) => p.nombre.toLowerCase().includes(q) || (p.clienteNombre || '').toLowerCase().includes(q))
      : proyectos;
    return lista.slice(0, MAX_RESULTADOS);
  }, [proyectos, q, hayBusqueda]);

  if (profile && profile.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <h1 className="text-xl font-semibold text-white flex items-center gap-2">
        <LifeBuoy className="w-5 h-5 text-blue-400" /> Soporte
      </h1>

      <div className="relative">
        <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por email, nombre de usuario, organización o proyecto..."
          className="w-full bg-zinc-800 border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
        />
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando datos de la plataforma...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ResultColumn
            titulo="Usuarios"
            icon={<User className="w-4 h-4" />}
            vacio="Ningún usuario coincide."
          >
            {usuariosFiltrados.map((u) => (
              <button
                key={u.uid}
                onClick={() => setSeleccion({ tipo: 'usuario', item: u })}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  seleccion?.tipo === 'usuario' && seleccion.item.uid === u.uid ? 'bg-blue-500/15 text-blue-300' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                <p className="truncate">{u.name || u.email}</p>
                {u.name && <p className="text-xs text-gray-500 truncate">{u.email}</p>}
              </button>
            ))}
          </ResultColumn>

          <ResultColumn
            titulo="Organizaciones"
            icon={<Building2 className="w-4 h-4" />}
            vacio="Ninguna organización coincide."
          >
            {organizacionesFiltradas.map((o) => (
              <button
                key={o.id}
                onClick={() => setSeleccion({ tipo: 'organizacion', item: o })}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  seleccion?.tipo === 'organizacion' && seleccion.item.id === o.id ? 'bg-blue-500/15 text-blue-300' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                {o.name}
              </button>
            ))}
          </ResultColumn>

          <ResultColumn
            titulo="Proyectos"
            icon={<FolderKanban className="w-4 h-4" />}
            vacio="Ningún proyecto coincide."
          >
            {proyectosFiltrados.map((p) => (
              <button
                key={p.id}
                onClick={() => setSeleccion({ tipo: 'proyecto', item: p })}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  seleccion?.tipo === 'proyecto' && seleccion.item.id === p.id ? 'bg-blue-500/15 text-blue-300' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                <p className="truncate">{p.nombre}</p>
                {p.clienteNombre && <p className="text-xs text-gray-500 truncate">{p.clienteNombre}</p>}
              </button>
            ))}
          </ResultColumn>
        </div>
      )}

      {seleccion?.tipo === 'usuario' && <DetalleUsuario usuario={seleccion.item} proyectos={proyectos} />}
      {seleccion?.tipo === 'organizacion' && <DetalleOrganizacion organizacion={seleccion.item} proyectos={proyectos} />}
      {seleccion?.tipo === 'proyecto' && <DetalleProyectoSoporte proyecto={seleccion.item} />}

      <TicketsAdminSection />
    </div>
  );
}

const TICKET_ESTADO_BADGE: Record<string, string> = {
  'Abierto': 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  'En revisión': 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  'Resuelto': 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  'Cerrado': 'bg-white/5 text-gray-400 border-white/10'
};

/**
 * Tickets de PLATAFORMA abiertos desde "Ayuda" (ver AyudaHome.tsx); los del
 * día a día del cliente van a la Mesa de ayuda del proyecto. A diferencia de la
 * búsqueda de arriba (usuarios/orgs/proyectos), acá no hace falta buscar
 * nada: son pocos y el Admin necesita verlos todos para triage. Sin
 * asignación (decisión de Fase 0): el único cambio posible es el estado.
 */
function TicketsAdminSection() {
  const [tickets, setTickets] = useState<Ticket[] | null>(null);

  const cargar = () => {
    getAllTicketsAdmin().then(setTickets).catch((err) => console.error('Error cargando tickets:', err));
  };

  useEffect(() => { cargar(); }, []);

  const handleEstadoChange = async (ticketId: string, estado: TicketEstado) => {
    setTickets((prev) => prev ? prev.map((t) => (t.id === ticketId ? { ...t, estado } : t)) : prev);
    try {
      await updateTicketEstado(ticketId, estado);
    } catch (err) {
      console.error('Error actualizando estado del ticket:', err);
      cargar();
    }
  };

  return (
    <div className="space-y-3 pt-4 border-t border-white/10">
      <h2 className="text-base font-semibold text-white flex items-center gap-2">
        <LifeBuoy className="w-4 h-4 text-blue-400" /> Tickets de soporte ({tickets?.length ?? '...'})
      </h2>
      {tickets === null ? (
        <p className="text-xs text-gray-500">Cargando...</p>
      ) : tickets.length === 0 ? (
        <p className="text-xs text-gray-500">No hay tickets todavía.</p>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <div key={t.id} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${TICKET_ESTADO_BADGE[t.estado]}`}>{t.estado}</span>
                  <span className="text-xs text-gray-500">{t.prioridad} · {t.categoria}{t.proyectoNombre ? ` · ${t.proyectoNombre}` : ''}</span>
                </div>
                <select
                  value={t.estado}
                  onChange={(e) => handleEstadoChange(t.id, e.target.value as TicketEstado)}
                  className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500"
                >
                  {TICKET_ESTADOS.map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <p className="text-sm text-gray-300 whitespace-pre-wrap">{t.descripcion}</p>
              <p className="text-xs text-gray-500">{t.creadoPorNombre} ({t.creadoPorEmail})</p>
              <TicketConversacion ticket={t} miRol="soporte" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultColumn({ titulo, icon, vacio, children }: { titulo: string; icon: React.ReactNode; vacio: string; children: React.ReactNode }) {
  const tieneContenido = React.Children.count(children) > 0;
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="px-3 py-2.5 border-b border-white/10 flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
        {icon} {titulo}
      </div>
      <div className="p-2 max-h-80 overflow-y-auto space-y-0.5">
        {tieneContenido ? children : <p className="text-xs text-gray-500 px-2 py-2">{vacio}</p>}
      </div>
    </div>
  );
}

function DetalleUsuario({ usuario, proyectos }: { usuario: UserProfile; proyectos: Project[] }) {
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [incidentes, setIncidentes] = useState<IncidenteConProyecto[] | null>(null);

  useEffect(() => {
    setOrgs(null);
    setIncidentes(null);
    getUserOrganizations(usuario.uid).then(setOrgs).catch((err) => console.error('Error cargando organizaciones del usuario:', err));
    buscarIncidentesPorEmailAdmin(usuario.email).then(setIncidentes).catch((err) => console.error('Error cargando incidentes del usuario:', err));
  }, [usuario.uid, usuario.email]);

  const proyectosDelUsuario = proyectos.filter((p) => p.miembrosUids.includes(usuario.uid));

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
      <div>
        <h3 className="text-base font-semibold text-white">{usuario.name || usuario.email}</h3>
        <p className="text-sm text-gray-400">{usuario.email} · {usuario.role === 'admin' ? 'Admin de plataforma' : 'Consultor'}</p>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Organizaciones ({orgs?.length ?? '...'})</p>
        {!orgs ? <p className="text-xs text-gray-500">Cargando...</p> : orgs.length === 0 ? <p className="text-xs text-gray-500">No pertenece a ninguna.</p> : (
          <div className="flex flex-wrap gap-1.5">
            {orgs.map((o) => (
              <span key={o.id} className="text-xs px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">{o.name} · {o.role}</span>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Proyectos ({proyectosDelUsuario.length})</p>
        {proyectosDelUsuario.length === 0 ? <p className="text-xs text-gray-500">No es miembro de ningún proyecto.</p> : (
          <div className="space-y-1.5">
            {proyectosDelUsuario.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 bg-black/20 border border-white/10 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-200 truncate">{p.nombre}</span>
                <Link to={`/proyecto/${p.id}`} className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 flex-shrink-0">
                  <LogIn className="w-3 h-3" /> Entrar
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" /> Incidentes reportados ({incidentes?.length ?? '...'})
        </p>
        {!incidentes ? <p className="text-xs text-gray-500">Cargando...</p> : incidentes.length === 0 ? <p className="text-xs text-gray-500">No reportó ningún incidente.</p> : (
          <div className="space-y-1.5">
            {incidentes.map((inc) => (
              <Link
                key={`${inc.projectId}_${inc.id}`}
                to={`/proyecto/${inc.projectId}/incidentes?incidenteId=${inc.id}`}
                className="block bg-black/20 border border-white/10 rounded-lg px-3 py-2 hover:bg-black/30 transition-colors"
              >
                <p className="text-sm text-gray-200 truncate">{inc.titulo}</p>
                <p className="text-xs text-gray-500">{inc.estado} · proyecto {inc.projectId}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DetalleOrganizacion({ organizacion, proyectos }: { organizacion: OrganizacionSinRol; proyectos: Project[] }) {
  const [miembros, setMiembros] = useState<OrgMemberConNombre[] | null>(null);

  useEffect(() => {
    setMiembros(null);
    getOrgMembers(organizacion.id).then(setMiembros).catch((err) => console.error('Error cargando miembros de la organización:', err));
  }, [organizacion.id]);

  const proyectosDeLaOrg = proyectos.filter((p) => p.orgId === organizacion.id);

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
      <h3 className="text-base font-semibold text-white">{organizacion.name}</h3>

      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Miembros ({miembros?.length ?? '...'})</p>
        {!miembros ? <p className="text-xs text-gray-500">Cargando...</p> : (
          <div className="flex flex-wrap gap-1.5">
            {miembros.map((m) => (
              <span key={m.id} className="text-xs px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">{m.displayName} · {m.role}</span>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Proyectos ({proyectosDeLaOrg.length})</p>
        {proyectosDeLaOrg.length === 0 ? <p className="text-xs text-gray-500">Sin proyectos.</p> : (
          <div className="space-y-1.5">
            {proyectosDeLaOrg.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 bg-black/20 border border-white/10 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-200 truncate">{p.nombre}</span>
                <Link to={`/proyecto/${p.id}`} className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 flex-shrink-0">
                  <LogIn className="w-3 h-3" /> Entrar
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DetalleProyectoSoporte({ proyecto }: { proyecto: Project }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-white">{proyecto.nombre}</h3>
          <p className="text-sm text-gray-400">{proyecto.clienteNombre || 'Cliente desconocido'} · {proyecto.miembros.length} miembro{proyecto.miembros.length !== 1 ? 's' : ''}</p>
        </div>
        <Link
          to={`/proyecto/${proyecto.id}`}
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all flex-shrink-0"
        >
          <LogIn className="w-4 h-4" /> Entrar (vista admin)
        </Link>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {proyecto.miembros.map((m) => (
          <span key={m.userId} className="text-xs px-2 py-1 rounded-full bg-white/5 text-gray-300 border border-white/10">{m.email} · {m.rol}</span>
        ))}
      </div>
    </div>
  );
}
